// The sticky bar has no variant pickers — the main product form is the source
// of truth. The bar's button mirrors the main ATC (label, disabled, sold-out
// state) and its quantity stepper drives the form's quantity input, so
// clicking the sticky button adds the currently selected variant.

import { throttle } from 'throttle-debounce';
import { SETTINGS } from '../utils/constants';

// The sticky button keeps its own identity/behaviour attributes through syncs
const SYNC_SKIPPED_ATTRIBUTES = ['class', 'type', 'data-sticky-atc-btn'];

const syncStickyButton = (stickyAtcButton, mainAtcButton, stickyQtyButtonEls) => {
  for (const name of stickyAtcButton.getAttributeNames()) {
    if (!SYNC_SKIPPED_ATTRIBUTES.includes(name)) {
      stickyAtcButton.removeAttribute(name);
    }
  }
  for (const name of mainAtcButton.getAttributeNames()) {
    if (!SYNC_SKIPPED_ATTRIBUTES.includes(name)) {
      stickyAtcButton.setAttribute(name, mainAtcButton.getAttribute(name));
    }
  }
  stickyAtcButton.innerHTML = mainAtcButton.innerHTML;
  stickyQtyButtonEls.forEach(buttonEl => (buttonEl.disabled = mainAtcButton.disabled));
};

const initScrollVisibility = (stickyAtc, productControlsEl) => {
  if (!productControlsEl) return;

  const getBottom = el => el.getBoundingClientRect().top + window.scrollY + el.offsetHeight;
  // The controls are always rendered (a sold-out variant shows a disabled
  // button rather than hiding them), so the bar appears once they scroll out.
  const updateVisibility = () => {
    const isPastControls = window.scrollY >= getBottom(productControlsEl);
    stickyAtc.classList.toggle('hidden', !isPastControls);
  };
  window.addEventListener('scroll', throttle(SETTINGS.THROTTLE_DELAY_SMALL, updateVisibility), {
    passive: true
  });
  updateVisibility();
};

const initQtySync = (stickyAtc, mainQtySelector) => {
  const mainQtyInput = mainQtySelector?.querySelector('[data-quantity-input]');
  const stickyQtyValueEl = stickyAtc.querySelector('[data-sticky-qty-value]');
  if (!mainQtyInput || !stickyQtyValueEl) return;

  const syncQtyValue = () => {
    stickyQtyValueEl.textContent = mainQtyInput.value;
  };

  // Registered after initQuantityInputs' own click handler, so the value is
  // already stepped by the time these sync reads run.
  mainQtySelector.addEventListener('click', syncQtyValue);
  mainQtyInput.addEventListener('change', syncQtyValue);

  stickyAtc.querySelector('[data-sticky-qty-dec]')?.addEventListener('click', () => {
    mainQtySelector.querySelector('[data-quantity-dec]')?.click();
    syncQtyValue();
  });
  stickyAtc.querySelector('[data-sticky-qty-inc]')?.addEventListener('click', () => {
    mainQtySelector.querySelector('[data-quantity-inc]')?.click();
    syncQtyValue();
  });
  syncQtyValue();
};

const initProductStickyAtc = () => {
  const stickyAtc = document.querySelector('[data-sticky-atc-wrapper]');
  if (!stickyAtc) return;

  const form = document.querySelector('[data-product-main] [data-product-form]');
  const mainAtcButton = form?.querySelector('button[type="submit"]');
  const stickyAtcButton = stickyAtc.querySelector('[data-sticky-atc-btn]');
  if (!form || !mainAtcButton || !stickyAtcButton) return;

  const stickyQtyButtonEls = stickyAtc.querySelectorAll('[data-sticky-qty-dec], [data-sticky-qty-inc]');

  const mainAtcBtnObserver = new MutationObserver(() =>
    syncStickyButton(stickyAtcButton, mainAtcButton, stickyQtyButtonEls)
  );
  syncStickyButton(stickyAtcButton, mainAtcButton, stickyQtyButtonEls);
  mainAtcBtnObserver.observe(mainAtcButton, {
    attributes: true,
    childList: true,
    subtree: true,
    characterData: true
  });

  initScrollVisibility(stickyAtc, document.querySelector('[data-product-main] [data-product-controls]'));
  initQtySync(stickyAtc, form.querySelector('[data-quantity-selector]'));

  stickyAtcButton.addEventListener('click', () => mainAtcButton.click());
};

export default initProductStickyAtc;
